import React, { useState, useEffect } from 'react'
import { useChatStore } from '../lib/store'
import { LLMProvider } from '../types/llm'
import { getAllProviders } from '../config/providers'

interface ConfigModalProps {
  onClose: () => void
}

const ConfigModal: React.FC<ConfigModalProps> = ({ onClose }) => {
  const { 
    selectedProvider, 
    selectedModel, 
    apiKeys,
    customProviderConfig,
    setProvider, 
    setModel, 
    setApiKey,
    setCustomProviderConfig,
    createSession 
  } = useChatStore()

  const [localProvider, setLocalProvider] = useState<LLMProvider | null>(selectedProvider)
  const [localModel, setLocalModel] = useState<string>(selectedModel || '')
  const [localApiKeys, setLocalApiKeys] = useState<Record<LLMProvider, string>>(apiKeys)
  const [customBaseUrl, setCustomBaseUrl] = useState(customProviderConfig.baseUrl || '')
  const [showApiKey, setShowApiKey] = useState<Record<LLMProvider, boolean>>({} as Record<LLMProvider, boolean>)

  const providers = getAllProviders()
  const currentProviderInfo = providers.find(p => p.id === localProvider)

  useEffect(() => {
    if (localProvider && currentProviderInfo && !localModel) {
      if (localProvider === LLMProvider.Custom && customProviderConfig.model) {
        setLocalModel(customProviderConfig.model)
      } else {
        setLocalModel(currentProviderInfo.defaultModel)
      }
    }
  }, [localProvider, currentProviderInfo, localModel, customProviderConfig])

  const handleSave = () => {
    if (!localProvider || !localModel) return

    // Save all settings
    setProvider(localProvider)
    setModel(localModel)
    
    // Save API keys
    Object.entries(localApiKeys).forEach(([provider, key]) => {
      if (key) {
        setApiKey(provider as LLMProvider, key)
      }
    })

    // Save custom provider config
    if (localProvider === LLMProvider.Custom) {
      setCustomProviderConfig({
        baseUrl: customBaseUrl,
        model: localModel
      })
    }

    // Create a new session if none exists
    createSession(localProvider, localModel)
    
    onClose()
  }

  const isValid = localProvider && localModel && localApiKeys[localProvider] && 
    (localProvider !== LLMProvider.Custom || customBaseUrl)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Configuration
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Provider Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              LLM Provider
            </label>
            <div className="grid grid-cols-2 gap-3">
              {providers.map((provider) => (
                <button
                  key={provider.id}
                  onClick={() => setLocalProvider(provider.id)}
                  className={`p-3 rounded-lg border-2 text-left transition-colors ${
                    localProvider === provider.id
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {provider.name}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {provider.description}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Model Selection */}
          {localProvider && currentProviderInfo && localProvider !== LLMProvider.Custom && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Model
              </label>
              <select
                value={localModel}
                onChange={(e) => setLocalModel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              >
                {currentProviderInfo.models.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* API Key */}
          {localProvider && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                API Key
              </label>
              <div className="relative">
                <input
                  type={showApiKey[localProvider] ? 'text' : 'password'}
                  value={localApiKeys[localProvider] || ''}
                  onChange={(e) => setLocalApiKeys({
                    ...localApiKeys,
                    [localProvider]: e.target.value
                  })}
                  placeholder="Enter your API key"
                  className="w-full pr-10 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey({
                    ...showApiKey,
                    [localProvider]: !showApiKey[localProvider]
                  })}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  {showApiKey[localProvider] ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Your API key is stored locally in your browser
              </p>
            </div>
          )}

          {/* Custom Base URL */}
          {localProvider === LLMProvider.Custom && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Base URL
              </label>
              <input
                type="url"
                value={customBaseUrl}
                onChange={(e) => setCustomBaseUrl(e.target.value)}
                placeholder="https://api.example.com/v1"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              />
            </div>
          )}

          {/* Custom Model Name */}
          {localProvider === LLMProvider.Custom && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Model Name
              </label>
              <input
                type="text"
                value={localModel}
                onChange={(e) => setLocalModel(e.target.value)}
                placeholder="e.g., llama-2-70b, gpt-3.5-turbo"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Enter the exact model name required by your API provider
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  )
}

export default ConfigModal
